import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Public,
  Lock,
  Visibility,
  Close
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { useConfirm } from '../../hooks/useConfirm';
import { generateClient } from 'aws-amplify/api';
import { userWatchlistsByUserIDAndCreatedAt, listUserWatchlists } from '../../graphql/queries';
import { enrichWatchlistsWithCounts } from '../../utils/watchlistCountUtils';
import { watchlistService } from '../../services/watchlistService';
import WatchlistViewer from './WatchlistViewer';

const WatchlistManager = ({ watchlist, createMode, onClose, initialWatchlists = [] }) => {
  const { user } = useAuth();
  const { addNotification } = useApp();
  const { confirm } = useConfirm();
  const theme = useTheme();
  const [watchlists, setWatchlists] = useState(initialWatchlists);
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, watchlist: null });
  const [formData, setFormData] = useState({ name: '', description: '', isPublic: false });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedWatchlist, setSelectedWatchlist] = useState(null);
  const [viewerWatchlist, setViewerWatchlist] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (initialWatchlists.length > 0) {
      setWatchlists(initialWatchlists);
    } else if (user && watchlists.length === 0) {
      loadWatchlists();
    }
    return () => {
      mountedRef.current = false;
    };
  }, [user, initialWatchlists]);
  
  useEffect(() => {
    if (watchlist) {
      setFormData({
        name: watchlist.name,
        description: watchlist.description || '',
        isPublic: watchlist.isPublic
      });
      setEditDialog({ open: true, watchlist });
    }
    if (createMode && !watchlist) {
      setFormData({ name: '', description: '', isPublic: false });
      setEditDialog({ open: true, watchlist: null });
    }
  }, [watchlist, createMode]);

  const loadWatchlists = useCallback(async () => {
    setLoading(true);
    try {
      const client = generateClient();
      const result = await client.graphql({
        query: listUserWatchlists,
        variables: {
          filter: { userID: { eq: user.id } },
          limit: 50
        }
      });
      
      let userWatchlists = result.data?.listUserWatchlists?.items || [];
      
      console.log('Loaded watchlists:', userWatchlists.length);
      
      // Create default watchlist if none exist or no default exists
      const hasDefault = userWatchlists.some(w => w.isDefault);
      if (userWatchlists.length === 0 || !hasDefault) {
        console.log('Creating default watchlist...');
        const defaultWatchlist = await createDefaultWatchlist();
        if (defaultWatchlist && !userWatchlists.find(w => w.id === defaultWatchlist.id)) {
          userWatchlists.push(defaultWatchlist);
          console.log('Default watchlist added');
        }
      }
      
      // Enrich with actual item counts
      const enrichedWatchlists = await enrichWatchlistsWithCounts(userWatchlists);
      console.log('Final watchlists:', enrichedWatchlists.length);
      console.log('Setting watchlists state:', enrichedWatchlists);
      
      if (mountedRef.current) {
        console.log('About to set watchlists:', enrichedWatchlists);
        setWatchlists(enrichedWatchlists);
        console.log('Watchlists state set');
      }
    } catch (error) {
      console.error('Error loading watchlists:', error);
      addNotification({ type: 'error', message: 'Failed to load watchlists' });
    } finally {
      setLoading(false);
    }
  }, [user, addNotification]);
  
  const createDefaultWatchlist = async () => {
    try {
      console.log('Creating default watchlist for user:', user.id);
      
      const result = await watchlistService.createWatchlist({
        userID: user.id,
        name: 'Want to Watch',
        description: 'Movies and shows I want to watch',
        isDefault: true,
        isPublic: false
      });
      
      console.log('Default watchlist created:', result);
      return result;
    } catch (error) {
      console.error('Error creating default watchlist:', error);
      return null;
    }
  };

  const handleCreateWatchlist = () => {
    setFormData({ name: '', description: '', isPublic: false });
    setEditDialog({ open: true, watchlist: null });
  };

  const handleEditWatchlist = (watchlist) => {
    setFormData({
      name: watchlist.name,
      description: watchlist.description || '',
      isPublic: watchlist.isPublic
    });
    setEditDialog({ open: true, watchlist });
    setAnchorEl(null);
  };

  const handleSaveWatchlist = async () => {
    if (!formData.name.trim()) {
      addNotification({ type: 'error', message: 'Watchlist name is required' });
      return;
    }

    // Check for duplicate names (excluding current watchlist when editing)
    const duplicateName = watchlists.find(w => 
      w.name.toLowerCase() === formData.name.trim().toLowerCase() && 
      w.id !== editDialog.watchlist?.id
    );
    
    if (duplicateName) {
      addNotification({ type: 'error', message: 'A watchlist with this name already exists' });
      return;
    }

    setLoading(true);
    try {
      if (editDialog.watchlist) {
        // Update existing watchlist
        const updatedWatchlist = await watchlistService.updateWatchlist({
          id: editDialog.watchlist.id,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          isPublic: formData.isPublic
        });
        
        if (mountedRef.current) {
          setWatchlists(prev => prev.map(w => 
            w.id === editDialog.watchlist.id 
              ? { ...w, ...updatedWatchlist }
              : w
          ));
          
          addNotification({ type: 'success', message: 'Watchlist updated!' });
        }
      } else {
        // Create new watchlist
        const newWatchlist = await watchlistService.createWatchlist({
          userID: user.id,
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          isDefault: false,
          isPublic: formData.isPublic
        });
        
        if (mountedRef.current) {
          setWatchlists(prev => [...prev, { ...newWatchlist, itemCount: 0 }]);
          addNotification({ type: 'success', message: 'Watchlist created!' });
        }
      }
      
      if (mountedRef.current) {
        setEditDialog({ open: false, watchlist: null });
      }
    } catch (error) {
      console.error('Error saving watchlist:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      const errorMessage = error.errors?.[0]?.message || error.message || 'Failed to save watchlist';
      addNotification({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWatchlist = async (watchlist) => {
    if (watchlist.isDefault) {
      addNotification({ type: 'error', message: 'Cannot delete default watchlist' });
      return;
    }

    // Confirm deletion
    const confirmed = await confirm({
      title: 'Delete Watchlist',
      message: `Are you sure you want to delete "${watchlist.name}"? This action cannot be undone and will remove all ${watchlist.itemCount || 0} items from this watchlist.`,
      confirmText: 'Delete',
      confirmColor: 'error',
      variant: 'error'
    });
    
    if (!confirmed) {
      setAnchorEl(null);
      return;
    }

    setLoading(true);
    try {
      await watchlistService.deleteWatchlist(watchlist.id);
      
      if (mountedRef.current) {
        setWatchlists(prev => prev.filter(w => w.id !== watchlist.id));
        addNotification({ 
          type: 'success', 
          message: `Watchlist "${watchlist.name}" deleted successfully!` 
        });
      }
    } catch (error) {
      console.error('Error deleting watchlist:', error);
      addNotification({ type: 'error', message: 'Failed to delete watchlist' });
    } finally {
      setLoading(false);
      setAnchorEl(null);
    }
  };

  const handleMenuClick = (event, watchlist) => {
    setAnchorEl(event.currentTarget);
    setSelectedWatchlist(watchlist);
  };

  const handleViewWatchlist = (watchlist) => {
    setViewerWatchlist(watchlist);
    setShowViewer(true);
    setAnchorEl(null);
  };

  const handleCloseViewer = () => {
    setShowViewer(false);
    setViewerWatchlist(null);
  };

  return (
    <>
    <Box sx={{ 
      p: { xs: 2, sm: 3 },
      background: theme.palette.mode === 'dark' 
        ? 'rgba(0, 0, 0, 0.7)' 
        : 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(15px)',
      border: theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 193, 7, 0.2)'
        : '1px solid rgba(25, 118, 210, 0.2)',
      borderRadius: 2,
      minHeight: '60vh'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Box>
          <Typography variant="h6">My Watchlists</Typography>
          <Typography variant="body2" color="text.secondary">
            {watchlists.length} watchlist{watchlists.length !== 1 ? 's' : ''} • {watchlists.reduce((sum, w) => sum + (w.itemCount || 0), 0)} total items
          </Typography>
        </Box>
        <Button
          startIcon={<Add />}
          variant="contained"
          onClick={handleCreateWatchlist}
          disabled={loading}
          size={window.innerWidth < 600 ? 'small' : 'medium'}
          fullWidth={window.innerWidth < 600}
          sx={{
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(45deg, #FFD700, #FFA000)'
              : 'linear-gradient(45deg, #1976d2, #42a5f5)',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(45deg, #FFA000, #FFD700)'
                : 'linear-gradient(45deg, #42a5f5, #1976d2)'
            }
          }}
        >
          Create Watchlist
        </Button>
      </Box>

      {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : watchlists.length === 0 ? (
        <Box sx={{ 
          textAlign: 'center', 
          py: 4,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(0, 0, 0, 0.3)' 
            : 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 193, 7, 0.1)'
            : '1px solid rgba(25, 118, 210, 0.1)'
        }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Watchlists Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first watchlist to organize your favorite movies and shows
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateWatchlist}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(45deg, #FFD700, #FFA000)'
                : 'linear-gradient(45deg, #1976d2, #42a5f5)',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(45deg, #FFA000, #FFD700)'
                  : 'linear-gradient(45deg, #42a5f5, #1976d2)'
              }
            }}
          >
            Create Your First Watchlist
          </Button>
        </Box>
        ) : (
        <Grid container spacing={2}>
          {watchlists.map((watchlist) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={watchlist.id}>
              <Card sx={{
                width: { xs: '100%', sm: 280 },
                height: { xs: 180, sm: 200 },
                display: 'flex',
                flexDirection: 'column',
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(0, 0, 0, 0.7)' 
                  : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(15px)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 193, 7, 0.2)'
                  : '1px solid rgba(25, 118, 210, 0.2)',
                '&:hover': {
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 32px rgba(255, 193, 7, 0.3)'
                    : '0 8px 32px rgba(25, 118, 210, 0.3)',
                  transform: 'translateY(-2px)',
                  border: theme.palette.mode === 'dark'
                    ? '1px solid rgba(255, 193, 7, 0.4)'
                    : '1px solid rgba(25, 118, 210, 0.4)'
                },
                transition: 'all 0.2s ease-in-out'
              }}>
                <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" noWrap sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      {watchlist.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, watchlist)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    {watchlist.isDefault && (
                      <Chip label="Default" size="small" color="primary" />
                    )}
                    {watchlist.isPublic ? (
                      <Chip icon={<Public />} label="Public" size="small" />
                    ) : (
                      <Chip icon={<Lock />} label="Private" size="small" />
                    )}
                  </Box>
                  
                  {watchlist.description && (
                    <Typography 
                      variant="body2"
                      sx={{ 
                        mb: { xs: 1, sm: 2 },
                        display: '-webkit-box',
                        WebkitLineClamp: { xs: 1, sm: 2 },
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                      color="text.secondary"
                    >
                      {watchlist.description}
                    </Typography>
                  )}
                  
                  <Typography variant="body2" color="text.secondary">
                    {watchlist.itemCount || 0} items
                  </Typography>
                  
                  <Typography variant="caption" color="text.secondary">
                    Created {new Date(watchlist.createdAt).toLocaleDateString()}
                  </Typography>
                </CardContent>
                
                <CardActions sx={{ gap: { xs: 0.5, sm: 1 }, p: { xs: 1, sm: 1.5 } }}>
                  <Button
                    size="small"
                    startIcon={<Visibility sx={{ display: { xs: 'none', sm: 'inline-flex' } }} />}
                    onClick={() => handleViewWatchlist(watchlist)}
                    sx={{
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(45deg, #FFD700, #FFA000)'
                        : 'linear-gradient(45deg, #1976d2, #42a5f5)',
                      color: 'white',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minWidth: { xs: 'auto', sm: 'auto' },
                      px: { xs: 1, sm: 2 },
                      '&:hover': {
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(45deg, #FFA000, #FFD700)'
                          : 'linear-gradient(45deg, #42a5f5, #1976d2)'
                      }
                    }}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Edit sx={{ display: { xs: 'none', sm: 'inline-flex' } }} />}
                    onClick={() => handleEditWatchlist(watchlist)}
                    variant="outlined"
                    sx={{
                      borderColor: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2',
                      color: theme.palette.mode === 'dark' ? '#FFD700' : '#1976d2',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      minWidth: { xs: 'auto', sm: 'auto' },
                      px: { xs: 1, sm: 2 },
                      '&:hover': {
                        borderColor: theme.palette.mode === 'dark' ? '#FFA000' : '#42a5f5',
                        background: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 215, 0, 0.1)' 
                          : 'rgba(25, 118, 210, 0.1)'
                      }
                    }}
                  >
                    Edit
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={() => handleViewWatchlist(selectedWatchlist)}>
          <Visibility sx={{ mr: 1 }} />
          View Watchlist
        </MenuItem>
        <MenuItem onClick={() => handleEditWatchlist(selectedWatchlist)}>
          <Edit sx={{ mr: 1 }} />
          Edit Watchlist
        </MenuItem>
        {selectedWatchlist && !selectedWatchlist.isDefault && (
          <MenuItem 
            onClick={() => handleDeleteWatchlist(selectedWatchlist)}
            sx={{ color: 'error.main' }}
          >
            <Delete sx={{ mr: 1 }} />
            Delete Watchlist
          </MenuItem>
        )}
        {selectedWatchlist && selectedWatchlist.isDefault && (
          <MenuItem disabled>
            <Lock sx={{ mr: 1 }} />
            Cannot delete default watchlist
          </MenuItem>
        )}
      </Menu>

      <Dialog 
        open={editDialog.open} 
        onClose={() => setEditDialog({ open: false, watchlist: null })} 
        maxWidth="sm" 
        fullWidth
        fullScreen={window.innerWidth < 600}
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100vh', sm: '90vh' },
            background: theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.9)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 193, 7, 0.3)'
              : '1px solid rgba(25, 118, 210, 0.3)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editDialog.watchlist ? 'Edit Watchlist' : 'Create New Watchlist'}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <TextField
            fullWidth
            label="Watchlist Name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
            required
            placeholder="e.g., Action Movies, Must Watch"
            helperText="Choose a descriptive name"
            size={window.innerWidth < 600 ? 'small' : 'medium'}
          />
          <TextField
            fullWidth
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            margin="normal"
            multiline
            rows={window.innerWidth < 600 ? 2 : 3}
            placeholder="Describe your watchlist..."
            size={window.innerWidth < 600 ? 'small' : 'medium'}
          />
          <Box sx={{ 
            mt: 2, 
            display: 'flex', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPublic}
                  onChange={(e) => setFormData(prev => ({ ...prev, isPublic: e.target.checked }))}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {formData.isPublic ? <Public /> : <Lock />}
                  {formData.isPublic ? 'Public' : 'Private'}
                </Box>
              }
            />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {formData.isPublic 
                ? 'Others can discover and view this watchlist'
                : 'Only you can see this watchlist'
              }
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Button 
            onClick={() => setEditDialog({ open: false, watchlist: null })} 
            disabled={loading}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveWatchlist}
            variant="contained"
            disabled={!formData.name.trim() || loading}
            startIcon={loading ? undefined : (editDialog.watchlist ? <Edit /> : <Add />)}
            size={window.innerWidth < 600 ? 'small' : 'medium'}
            sx={{
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(45deg, #FFD700, #FFA000)'
                : 'linear-gradient(45deg, #1976d2, #42a5f5)',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(45deg, #FFA000, #FFD700)'
                  : 'linear-gradient(45deg, #42a5f5, #1976d2)'
              }
            }}
          >
            {loading ? 'Saving...' : (editDialog.watchlist ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    
    {/* Watchlist Viewer */}
    {showViewer && (
      <WatchlistViewer
        watchlist={viewerWatchlist}
        onClose={handleCloseViewer}
        onEdit={(watchlist) => {
          handleCloseViewer();
          handleEditWatchlist(watchlist);
        }}
        onUpdate={loadWatchlists}
      />
    )}
    </>
  );
};

export default WatchlistManager;